/**
 * The agent's markdown, as something worth saying out loud, and the chunker that cuts a streaming reply
 * into sentences for text-to-speech.
 *
 * `toSpeakable`: links become their text, bare URLs "the link in the chat", code blocks and tables one
 * "I've put it in the chat", bullets and headings become sentences, emphasis markers, emoji and ids go.
 *
 * `SentenceChunker`: the first chunk goes out at the first sentence end (or ~80 chars at a clause break,
 * so the voice starts fast); later chunks hold one or two sentences, at most 300 chars. It never cuts
 * inside a code block or a link, after an abbreviation ("e.g.", "Mr."), or inside a number ("3.5").
 * Feeding it piece by piece gives the same chunks as feeding it all at once.
 */

export const IN_THE_CHAT = "I've put it in the chat."

function endSentence(s: string): string {
    const t = s.trim()
    if (!t) return ''
    return /[.!?:;…]["')\]]*$/.test(t) ? t : `${t}.`
}

/** Markdown → plain spoken sentences. '' when there's nothing to say. */
export function toSpeakable(md: string): string {
    let t = md.replace(/\r\n/g, '\n')
    // Code blocks (an unclosed fence runs to the end) and tables: one line pointing at the chat.
    t = t.replace(/```[\s\S]*?(?:```|$)/g, `\n${IN_THE_CHAT}\n`)
    t = t.replace(/(?:^|\n)(?:[ \t]*\|.*\|[ \t]*(?:\n|$))+/g, `\n${IN_THE_CHAT}\n`)
    // Images say nothing; links say their text; bare URLs point at the chat.
    t = t.replace(/!\[[^\]]*\]\((?:[^()]|\([^)]*\))*\)/g, '')
    t = t.replace(/\[([^\]]+)\]\((?:[^()]|\([^)]*\))*\)/g, '$1')
    t = t.replace(/<?\bhttps?:\/\/[^\s<>)\]]+>?/gi, 'the link in the chat')
    t = t.replace(/\bwww\.[^\s<>)\]]+/gi, 'the link in the chat')
    t = t.replace(/`([^`\n]+)`/g, '$1')
    // Structure → sentences.
    t = t.replace(/^[ \t]{0,3}#{1,6}[ \t]+(.+?)[ \t]*#*[ \t]*$/gm, (_m, h: string) => endSentence(h))
    t = t.replace(/^[ \t]*>[ \t]?/gm, '')
    t = t.replace(/^[ \t]*([-*_])[ \t]*(?:\1[ \t]*){2,}$/gm, '')
    t = t.replace(/^[ \t]*(?:[-*+•]|\d{1,2}[.)])[ \t]+(.+)$/gm, (_m, item: string) => endSentence(item))
    // Emphasis.
    t = t.replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2')
    t = t.replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?!\w)/g, '$1$2')
    t = t.replace(/(^|[^\w])_(?=\S)([^_\n]*?\S)_(?!\w)/g, '$1$2')
    t = t.replace(/~~(.+?)~~/g, '$1')
    // Emoji, ids.
    t = t.replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}‍️⃣]/gu, '')
    t = t.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '')
    t = t.replace(/\b(?=[0-9a-f]*\d)(?=[0-9a-f]*[a-f])[0-9a-f]{12,}\b/gi, '')
    // Paragraphs end sentences; then collapse whitespace.
    t = t.replace(/([^\s.!?:;…])[ \t]*\n[ \t]*\n/g, '$1.\n\n')
    t = t.replace(/[*#]{2,}/g, '')
    t = t.replace(/\s+/g, ' ')
    t = t.replace(/\s+([.,!?;:])/g, '$1')
    t = t.replace(/([.!?])\.(?=\s|$)/g, '$1')
    // The same pointer twice in a row is said once.
    const dup = IN_THE_CHAT.replace(/[.']/g, (c) => `\\${c}`)
    t = t.replace(new RegExp(`(${dup})(?:\\s*${dup})+`, 'g'), '$1')
    return t.trim()
}

const ABBREVIATIONS = new Set([
    'e.g', 'i.e', 'mr', 'mrs', 'ms', 'dr', 'vs', 'etc', 'st', 'jr', 'sr', 'inc', 'ltd', 'co', 'no', 'approx', 'a.m', 'p.m',
    'u.s', 'u.k', 'fig', 'est', 'dept', 'mt',
])

export interface ChunkerOptions {
    /** First chunk: cut at a clause break past this many chars when no sentence has ended. */
    firstClauseAt?: number
    /** A single sentence at least this long goes out alone; shorter ones wait for a second. */
    shortSentence?: number
    max?: number
}

/** Positions where a chunk may end: after a sentence, a list item, a heading, or a code block. */
function scan(buf: string): { bounds: number[]; clauses: number[]; spaces: number[]; fenceOpen: boolean; linkOpen: boolean } {
    const bounds: number[] = []
    const clauses: number[] = []
    const spaces: number[] = []
    let fence = false
    let bracket = 0
    let paren = 0
    let lineStart = true
    for (let i = 0; i < buf.length; i++) {
        if (lineStart && buf.startsWith('```', i)) {
            if (!fence && i > 0) bounds.push(i)
            fence = !fence
            const nl = buf.indexOf('\n', i + 3)
            if (nl < 0) {
                i = buf.length
                break
            }
            if (!fence) bounds.push(nl + 1)
            i = nl
            lineStart = true
            continue
        }
        const c = buf[i]
        lineStart = c === '\n'
        if (fence) continue
        if (c === '[') bracket++
        else if (c === ']' && bracket > 0) {
            bracket--
            if (buf[i + 1] === '(') {
                paren++
                i++
            }
        } else if (c === ')' && paren > 0) paren--
        if (bracket || paren) continue
        if (c === '\n') {
            const rest = buf.slice(i + 1)
            if (/^\s*\n/.test(rest) || /^[ \t]*(?:[-*+•]|\d{1,2}[.)]|#{1,6})[ \t]/.test(rest)) bounds.push(i)
            continue
        }
        if (c === ' ' || c === '\t') {
            spaces.push(i)
            if (/[,;:—–]/.test(buf[i - 1] ?? '')) clauses.push(i)
            continue
        }
        if (/[.!?…]/.test(c)) {
            let j = i + 1
            while (j < buf.length && /[.!?…"'”’)\]*_]/.test(buf[j])) j++
            if (j >= buf.length || !/\s/.test(buf[j])) {
                i = j - 1
                continue
            }
            if (c === '.') {
                const word = /([A-Za-z.]+)$/.exec(buf.slice(Math.max(0, i - 12), i))?.[1]?.toLowerCase().replace(/^\.+/, '')
                if (word && (ABBREVIATIONS.has(word) || /^[a-z]$/.test(word))) {
                    i = j - 1
                    continue
                }
            }
            bounds.push(j)
            i = j - 1
        }
    }
    return { bounds, clauses, spaces, fenceOpen: fence, linkOpen: bracket > 0 || paren > 0 }
}

export class SentenceChunker {
    private buf = ''
    private first = true
    private readonly firstClauseAt: number
    private readonly shortSentence: number
    private readonly max: number

    constructor(opts: ChunkerOptions = {}) {
        this.firstClauseAt = opts.firstClauseAt ?? 80
        this.shortSentence = opts.shortSentence ?? 60
        this.max = opts.max ?? 300
    }

    /** Add streamed text; returns chunks that are ready to speak. */
    push(text: string): string[] {
        this.buf += text
        return this.drain(false)
    }

    /** The message is complete: everything left, as chunks. */
    flush(): string[] {
        const out = this.drain(true)
        const rest = this.buf.trim()
        this.buf = ''
        if (rest) out.push(rest)
        return out
    }

    get pending(): string {
        return this.buf
    }

    private drain(final: boolean): string[] {
        const out: string[] = []
        for (;;) {
            // Leading whitespace belongs to no chunk.
            const lead = this.buf.length - this.buf.trimStart().length
            if (lead) this.buf = this.buf.slice(lead)
            if (!this.buf) break
            const cut = this.cut(final)
            if (cut <= 0) break
            const chunk = this.buf.slice(0, cut).trim()
            this.buf = this.buf.slice(cut)
            if (chunk) {
                out.push(chunk)
                this.first = false
            }
        }
        return out
    }

    private soft(sc: ReturnType<typeof scan>, limit: number, from = 0): number {
        const clause = sc.clauses.filter((p) => p >= from && p <= limit).at(-1)
        if (clause) return clause
        const space = sc.spaces.filter((p) => p >= from && p <= limit).at(-1)
        return space ?? limit
    }

    private cut(final: boolean): number {
        const sc = scan(this.buf)
        const len = this.buf.length
        const b = sc.bounds.filter((p) => p > 0)
        if (this.first) {
            if (b.length && b[0] <= this.firstClauseAt) return b[0]
            if (len > this.firstClauseAt || (final && !b.length)) {
                const clause = sc.clauses.find((p) => p >= this.firstClauseAt * 0.3 && p <= this.firstClauseAt)
                if (clause) return clause
            }
        }
        if (b.length && b[0] <= this.max) {
            if (b[0] >= this.shortSentence) return b[0]
            if (b.length >= 2) return b[1] <= this.max ? b[1] : b[0]
            if (len > this.max || final) return b[0]
            return 0
        }
        // Never inside a code block (it all becomes one line); an unclosed "[" only holds so long.
        if (sc.fenceOpen) return final ? len : 0
        if (len > this.max && (!sc.linkOpen || final || len > this.max * 2)) return this.soft(sc, this.max, 1)
        return final ? len : 0
    }
}
